/**
 * FilePond Upload Component
 * 
 * A plug-and-play file upload component that works anywhere.
 * Just drop it in and it handles everything: upload, validation, error handling.
 * 
 * @example
 * ```tsx
 * <FilePondUpload
 *   onUploadComplete={(fileId, fileUrl, filename) => {
 *     // Do something with the uploaded file
 *   }}
 * />
 * ```
 */

import { useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { FilePond, registerPlugin } from 'react-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'

// Import styles
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// Register plugins once
registerPlugin(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
)

import { useUploadFile, useOpenUploadFile, useDeleteFile } from '@/utils/api/file-service'
import type { AxiosError } from 'axios'

export interface FilePondUploadProps {
  /** Called when file upload completes successfully */
  onUploadComplete?: (fileId: string, fileUrl: string, filename: string, fileSize: number) => void
  
  /** Called when upload fails */
  onUploadError?: (error: string) => void
  
  /** Called when a file is removed */
  onRemove?: (fileId: string) => void
  
  /** Accepted file types - defaults to images and PDFs */
  accept?: string[]
  
  /** Max file size (e.g., '5MB', '20MB') - defaults to '20MB' */
  maxFileSize?: string
  
  /** Allow multiple files - defaults to false */
  multiple?: boolean
  
  /** Max number of files when multiple is true - defaults to 5 */
  maxFiles?: number
  
  /** Disabled state */
  disabled?: boolean
  
  /** Custom label - supports HTML */
  label?: string
  
  /** Additional CSS classes */
  className?: string
  
  /** Show required indicator */
  required?: boolean

  /** User authentication status */
  isAuthenticated?: boolean

  /** Username or email for unauthenticated uploads */
  usernameOrEmail?: string
  /** Called when a file is added to FilePond (client-side) */
  onAddFile?: (file: File) => void
}

export function FilePondUpload({
  onUploadComplete,
  onUploadError,
  onRemove,
  onAddFile,
  accept = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
  maxFileSize = '20MB',
  multiple = false,
  maxFiles = 5,
  disabled = false,
  label,
  className = '',
  required = false,
  isAuthenticated = true,
  usernameOrEmail = "",
}: FilePondUploadProps) {
  const pondRef = useRef<FilePond>(null)
  const uploadMutation = useUploadFile()
  const openUploadMutation = useOpenUploadFile()
  const deleteMutation = useDeleteFile()
  const [isUploading, setIsUploading] = useState(false)
  
  // Store file IDs for removal
  const fileIdsRef = useRef<Map<string, string>>(new Map())

  // Generate default label if not provided
  const defaultLabel = `
    <div style="display: flex; flex-direction: column; align-items: center; gap: 0.5rem;">
      <span style="font-weight: 500;">
        Drag & Drop your ${multiple ? 'files' : 'file'} or 
        <span class="filepond--label-action">Browse</span>
        ${required ? '<span style="color: #ef4444; margin-left: 0.25rem;">*</span>' : ''}
      </span>
      <span style="font-size: 0.875rem; opacity: 0.7;">
        ${accept.includes('application/pdf') ? 'PDF, ' : ''}${accept.some(t => t.startsWith('image')) ? 'Images' : 'Files'} 
        (max ${maxFileSize})
      </span>
    </div>
  `

  return (
    <div className={`relative ${className}`}>
      <FilePond
        ref={pondRef}
        allowMultiple={multiple}
        maxFiles={multiple ? maxFiles : 1}
        maxFileSize={maxFileSize}
        acceptedFileTypes={accept}
        disabled={disabled}
        // instantUpload
        labelIdle={label || defaultLabel}
        name="file"
        credits={false}
        
        // Server configuration
        server={{
          process: async (
            _fieldName,
            file,
            _metadata,
            load,
            error,
            _progress,
            abort
          ) => {
            setIsUploading(true)
            try {
              // console.log("isAuthenticated: ", isAuthenticated);
              
              const response = isAuthenticated
                ? await uploadMutation.mutateAsync({ data: { file } })
                : await openUploadMutation.mutateAsync({ data: { file, usernameOrEmail } })

              // console.log("response: ", response);
              
              const fileData = response.data
              
              // Store the server file ID mapped to FilePond's unique file ID
              const serverId = fileData.fileId || ''
              fileIdsRef.current.set(file.name, serverId)
              
              // Notify success
              load(serverId)
              
              // Call the callback
              onUploadComplete?.(
                fileData.fileId || '',
                fileData.path || '',
                fileData.originalFilename || (file as File).name,
                fileData.size || 0
              )
            } catch (err) {
              const axiosError = err as AxiosError<{ message?: string }>
              const errorMessage = axiosError.response?.data?.message 
                || axiosError.message 
                || 'Upload failed. Please try again.'
              
              error(errorMessage)
              onUploadError?.(errorMessage)
            } finally {
              setIsUploading(false)
            }
            
            return {
              abort: () => {
                setIsUploading(false)
                abort()
              },
            }
          },
          
          revert: async (uniqueFileId, load, errorCallback) => {
            const serverId = (uniqueFileId as string) || ''
            try {
              await deleteMutation.mutateAsync({ fileId: serverId })
              onRemove?.(serverId)
              const entry = Array.from(fileIdsRef.current.entries()).find(
                ([_, id]) => id === serverId
              )
              if (entry) {
                fileIdsRef.current.delete(entry[0])
              }
              load()
            } catch (err) {
              const axiosError = err as AxiosError<{ message?: string }>
              const errorMessage =
                axiosError.response?.data?.message ||
                axiosError.message ||
                'Failed to delete file. Please try again.'

              if (typeof errorCallback === 'function') {
                errorCallback(errorMessage)
              }
              onUploadError?.(errorMessage)
            }
          },
        }}
        
        // Event handlers
        onprocessfilestart={() => setIsUploading(true)}
        onprocessfile={(error) => {
          setIsUploading(false)
          if (error) {
            console.error('FilePond processing error:', error)
          }
        }}
        onprocessfileabort={() => setIsUploading(false)}
        onprocessfilerevert={() => setIsUploading(false)}
        onaddfile={(_, fileItem) => {
          try {
            const fpFile = (fileItem && (fileItem as any).file) as File | undefined
            if (fpFile && typeof onAddFile === 'function') onAddFile(fpFile)
          } catch (e) {
            // ignore
          }
        }}
        onremovefile={(error) => {
          if (error) {
            console.error('FilePond removal error:', error)
          }
        }}
        
        // Styling
        stylePanelLayout="compact"
        styleButtonRemoveItemPosition="right"
        styleButtonProcessItemPosition="right"
        styleLoadIndicatorPosition="right"
        styleProgressIndicatorPosition="right"
      />
      {isUploading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-background/60">
          <div className="flex items-center gap-2 rounded-full bg-card/90 px-3 py-2 text-sm font-medium text-muted-foreground shadow">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span>Uploading…</span>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Simple wrapper for single file upload that auto-clears on success
 * Perfect for form fields where you only want one file at a time
 */
export function FilePondSingleUpload(props: Omit<FilePondUploadProps, 'multiple' | 'maxFiles'>) {
  return <FilePondUpload {...props} multiple={false} maxFiles={1} />
}

/**
 * Wrapper for image-only uploads
 */
export function FilePondImageUpload(props: Omit<FilePondUploadProps, 'accept'>) {
  return (
    <FilePondUpload
      {...props}
      accept={['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']}
    />
  )
}

/**
 * Wrapper for document uploads (PDFs and images)
 */
export function FilePondDocumentUpload(props: Omit<FilePondUploadProps, 'accept'>) {
  return (
    <FilePondUpload
      {...props}
      accept={['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']}
    />
  )
}
